import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type TipoDocumento = 'NFSE' | 'NFE' | 'NFAE' | 'OUTRO';

export interface DocumentoFiscal {
  id: string;
  workspaceId: string;
  tipo: TipoDocumento;
  numero: string | null;
  serie: string | null;
  chaveAcesso: string | null;
  emitenteCnpj: string;
  emitenteNome: string;
  valorTotal: string;
  dataEmissao: string;
  descricaoServicos: string | null;
  r2Key: string | null;
  ocrConfidence: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentosFiscaisListResult {
  items: DocumentoFiscal[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentosFiscaisAggregate {
  count: number;
  total: string;
  buckets: Record<TipoDocumento, { count: number; total: string }>;
}

export interface ListFilters {
  tipo?: TipoDocumento;
  search?: string;
  dataInicio?: string;
  dataFim?: string;
  limit?: number;
  offset?: number;
}

export interface CreateDocumentoFiscalInput {
  tipo: TipoDocumento;
  emitenteCnpj: string;
  emitenteNome: string;
  valorTotal: string;
  dataEmissao: string;
  numero?: string;
  serie?: string;
  chaveAcesso?: string;
  descricaoServicos?: string;
  r2Key?: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/documentos-fiscais`;
}

function buildQuery(filters: ListFilters): string {
  const q = new URLSearchParams();
  if (filters.tipo) q.set('tipo', filters.tipo);
  if (filters.search) q.set('search', filters.search);
  if (filters.dataInicio) q.set('dataInicio', filters.dataInicio);
  if (filters.dataFim) q.set('dataFim', filters.dataFim);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useDocumentosFiscais(
  workspaceId: string | null | undefined,
  filters: ListFilters,
) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['documentos-fiscais', workspaceId, filters],
    queryFn: () =>
      api.get<DocumentosFiscaisListResult>(
        `${basePath(workspaceId!)}/${buildQuery(filters)}`,
      ),
  });
}

export function useDocumentosFiscaisAggregate(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['documentos-fiscais', workspaceId, 'aggregate'],
    queryFn: () =>
      api.get<DocumentosFiscaisAggregate>(`${basePath(workspaceId!)}/aggregate`),
  });
}

export function useCreateDocumentoFiscal(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentoFiscalInput) =>
      api.post<DocumentoFiscal>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentos-fiscais', workspaceId] });
    },
  });
}

export function useDeleteDocumentoFiscal(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentos-fiscais', workspaceId] });
    },
  });
}
